import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Autocomplete, Breadcrumbs, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, Icon, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Tooltip } from '@mui/material';
import { GridCloseIcon } from '@mui/x-data-grid';

import { BestuurlijkeGebiedenRoute } from '../../../models/bestuurlijkeGebieden/bestuurlijkeGebiedenRoute';
import { BestuurlijkeGebiedenOrganisatie } from '../../../models/bestuurlijkeGebieden/bestuurlijkeGebiedenOrganisatie';
import { Organisatie } from '../../../models/organisatie/organisatie';

import { bestuurlijkeGebiedenService } from '../../../services/bestuurlijke-gebieden.service';
import { organisatieService } from '../../../services/organisaties.service';

import ProgressBar from '../ProgressBar';
import ErrorMessage from '../ErrorMessage';

interface DialogOrganisatieAddProps {
  isZonderGebied: boolean; // Optional prop to indicate if it's without area
  callback: () => void; // Callback function to trigger refresh
}

const DialogOrganisatieAdd: React.FC<DialogOrganisatieAddProps> = ({ 
  isZonderGebied,
  callback
 }) => {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = React.useState(false);

  const [routes, setRoutes] = useState<BestuurlijkeGebiedenRoute[]>([]);
  const [objRoute, setRoute] = useState<BestuurlijkeGebiedenRoute | null>(null);
  const [bestuurlijkeOrganisaties, setBestuurlijkeOrganisaties] = useState<BestuurlijkeGebiedenOrganisatie[]>([]);
  const [type, setOrganisatieType] = useState<string | null>(null);
  const [autocompleteType, setAutocompleteType] = useState('');
  const [selectedBestuurlijkeOrganisatie, setSelectedBestuurlijkeOrganisatie] = useState<BestuurlijkeGebiedenOrganisatie | null>(null);
  const [pathOrganisaties, setOrganisatiePath] = useState<BestuurlijkeGebiedenOrganisatie[]>([]);
  const [organisatie, setOrganisatie] = useState<Organisatie | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<{ id: number; message: string } | null>(null);

  const [validationErrors, setValidationErrors] = useState({
    route: '',
    organisatie: '',
    displayName: '',
  });

  const hasRoutesData = useRef(false);

  const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') {
      // Prevent closing when clicking on the backdrop
      return;
    }
    onClickOpenDialogAddOrganisatie(false);
  };
  
  const onClickOpenDialogAddOrganisatie = (open: boolean = false) => {
    setOpenDialog(open);
    if (open  === false) {
      setRoute(null);
      setSelectedBestuurlijkeOrganisatie(null);
      setBestuurlijkeOrganisaties([]);
      setOrganisatieType(null);
      setOrganisatie(null);
      setOrganisatiePath([]);

      setValidationErrors({ displayName: '', route: '', organisatie: '' });
    }
  };

  const onClickSelectNextUrl = () => {
    if (selectedBestuurlijkeOrganisatie) {
      let updatedPath = [...pathOrganisaties];
      if (selectedBestuurlijkeOrganisatie?.nextUrl) {
        fetchOrganizationsByRoute(selectedBestuurlijkeOrganisatie.nextUrl);
  
        updatedPath.push(selectedBestuurlijkeOrganisatie);
        setSelectedBestuurlijkeOrganisatie(null); // Clear selected organization
  
        const newAutocompleteType = selectedBestuurlijkeOrganisatie.nextUrl.split('/').pop();
        if (newAutocompleteType) {
          setAutocompleteType(newAutocompleteType);
        }
      }
      else {
        const organisatie = updatedPath.find(item => item.bestuurlijkGebiedType === selectedBestuurlijkeOrganisatie?.bestuurlijkGebiedType);
        if (organisatie) {
          // Replace this item in the path
          const index = updatedPath.findIndex(item => item.code === organisatie.code);
          if (index !== -1) {
            updatedPath[index] = selectedBestuurlijkeOrganisatie;
          }
        }
        else {
          updatedPath.push(selectedBestuurlijkeOrganisatie);
        }
      }
      setOrganisatiePath(updatedPath);
    }
  };

  const onClickDeletePathOrganisatie = (organisatie: BestuurlijkeGebiedenOrganisatie) => {
    if (organisatie) {
      if (organisatie?.nextUrl) {
        // Remove all items from this level and below
        const index = pathOrganisaties.findIndex(item => item.nextUrl === organisatie.nextUrl);
        if (index !== -1) {
          const updatedPathOrganisaties = pathOrganisaties.slice(0, index);
          setOrganisatiePath(updatedPathOrganisaties);
  
          if (updatedPathOrganisaties.length > 0) {
            // Get last item in the path is the current selected path item
            const newCurrentOrganisation = updatedPathOrganisaties[updatedPathOrganisaties.length - 1];
            if (newCurrentOrganisation.nextUrl) {
              fetchOrganizationsByRoute(newCurrentOrganisation.nextUrl);
  
              const newAutocompleteType = newCurrentOrganisation.nextUrl.split('/').pop();
              if (newAutocompleteType) {
                setAutocompleteType(newAutocompleteType);
              }
            }
            setSelectedBestuurlijkeOrganisatie(null); // Clear selected organization
          }
          else if (objRoute) {
            fetchOrganizationsByRoute(objRoute.route);
            setAutocompleteType(objRoute.name);
            setSelectedBestuurlijkeOrganisatie(null); // Clear selected organization
          }
        }
      }
      else {
        // Remove the selected organization from the path
        const updatedPathOrganisaties = pathOrganisaties.filter(item => item.code !== organisatie.code);
        setOrganisatiePath(updatedPathOrganisaties);
        setSelectedBestuurlijkeOrganisatie(null); // Clear selected organization
      }
    }
  };

  const validateForm = (overrides?: Partial<Organisatie>) => {
    const org = { ...organisatie, ...overrides };

    const displayNameError =
      (org?.displayName && org.displayName.trim().length >= 3)
        ? ''
        : 'Weergave naam is verplicht en moet minstens 3 karakters zijn.';
  
    if (isZonderGebied) {
      const nameRegex = /^(?!.*--)[a-z0-9](?:[a-z0-9-]{1,18}[a-z0-9])$/;
      let organisatieError = '';
  
      if (!org?.name) {
        organisatieError = 'Naam is verplicht en moet minstens 3 karakters zijn.';
      }
      else if (!nameRegex.test(org.name)) {
        organisatieError = 'Naam moet 3-20 karakters bevatten, alleen kleine letters, cijfers en streepjes. Geen dubbele streepjes toegestaan.';
      }
  
      return {
        displayName: displayNameError,
        organisatie: organisatieError,
        route: '' // route check skipped for zonder-gebied
      };
    }
  
    return {
      displayName: displayNameError,
      organisatie:
        (!isZonderGebied && !selectedBestuurlijkeOrganisatie)
          ? 'Selecteer een organisatie.'
          : '',
      route:
        (!isZonderGebied && !objRoute)
          ? 'Selecteer een bestuurlijke gebied.'
          : ''
    };
  };
  

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.values(errors).some((error) => error !== '')) {
      setValidationErrors(errors);
      return;
    }

    if (!organisatie) {
      console.error('Organisatie is null');
      return;
    }

    setLoading(true);

    try {
      const response = await organisatieService.updateOrganisatie(organisatie, isZonderGebied);

      if ('error' in response) {
        setLoading(false);
        if (response.status === 400) {
          setError({ id: Date.now(), message: response.message });
          return;
        }
    
        // Fallback voor andere fouten
        setError({ id: Date.now(), message: response.message || 'Er is een fout opgetreden' });
        return;
      }

      setLoading(false);
      onClickOpenDialogAddOrganisatie(false);
      //console.log('Organisatie updated successfully:', response);

      // Trigger refresh in the parent component
      if (callback) callback();
    }
    catch (error: any) {
      setLoading(false);
      // Alleen bij netwerkfouten of hard exceptions
      console.error('Er is een fout opgetreden:', error);
      setError({ id: Date.now(), message: 'Er is een fout opgetreden' });
    }
  }

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value;
    setOrganisatie({ ...organisatie, name });
    
    // Revalidate only organisatie field (but validateForm still handles the logic)
    if (validationErrors && (validationErrors.organisatie || validationErrors.displayName)) {
      const errors = validateForm({ name });
      setValidationErrors((prev) => ({
        ...prev,
        organisatie: errors.organisatie,
      }));
    }
  }

  const handleChangeDisplayName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const displayName = event.target.value;
    setOrganisatie({ ...organisatie, displayName });
    
    setValidationErrors((prev) => ({
      ...prev,
      displayName: displayName.trim().length >= 3 ? '' : 'Weergave naam is verplicht en moet minstens 3 karakters zijn.',
    }));
  }

  const onChangeOrganisatie = (event: SelectChangeEvent<string>) => {
    const selectedOrganisatieCode = event.target.value;
    const objBestuurlijkeOrganisatie = bestuurlijkeOrganisaties.find((item) => item.code === selectedOrganisatieCode);
    if (objBestuurlijkeOrganisatie) {
      setSelectedBestuurlijkeOrganisatie(objBestuurlijkeOrganisatie);

      const reference = objBestuurlijkeOrganisatie.reference;
      const name = objBestuurlijkeOrganisatie.name;
      setOrganisatie({ ...organisatie, name, reference  });
      setValidationErrors((prev) => ({ ...prev, organisatie: '' }));
    }
    else {
      resetBestuurlijkeOrganisatie();
    }
  };

  const onChangeRoute = (event: SelectChangeEvent<string>) => {
    const selectedRoute = event.target.value;
    const objRoute = routes.find((item) => item.route === selectedRoute);
    if (objRoute) {
      setRoute(objRoute);

      setOrganisatie(null);
      setOrganisatiePath([]);
      setSelectedBestuurlijkeOrganisatie(null);
      setValidationErrors((prev) => ({ ...prev, route: '' }));
      setLoading(true);
      fetchOrganizationsByRoute(objRoute.route);

      // Set the type based on the route name
      setAutocompleteType(objRoute.name);
    }
    else {
      setRoute(null);
      setSelectedBestuurlijkeOrganisatie(null);
      setBestuurlijkeOrganisaties([]); // Clear organizations if no route is selected
      setOrganisatieType(null);
      setOrganisatie({ ...organisatie, name: null, reference: null });
      setOrganisatiePath([]);
      setValidationErrors((prev) => ({ ...prev, route: 'Bestuurlijke gebied is verplicht.' }));
    }
  };

  const fetchOrganizationsByRoute = async (route: string) => {
    try {
      setLoading(true);
      const data = await bestuurlijkeGebiedenService.getBestuurlijkeGebiedenByRoute(route);
      if (data && data.length > 0) {
        setBestuurlijkeOrganisaties(data);
  
        const bestuurlijkGebiedType = data[0]?.bestuurlijkGebiedType;
        const capitalizedType = bestuurlijkGebiedType ? bestuurlijkGebiedType.charAt(0).toUpperCase() + bestuurlijkGebiedType.slice(1) : '';
        setOrganisatieType(capitalizedType);
      }
    } 
    catch (error: any) {
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      console.error('Error fetching organisaties:', error);
      setError({ id: Date.now(), message: 'Geen organisaties gevonden voor deze soort' });
    }
    finally {
      setLoading(false);
    }
  };

  const resetBestuurlijkeOrganisatie = () => {
    setSelectedBestuurlijkeOrganisatie(null);
    setOrganisatie({ ...organisatie, name: null, reference: null });
    setValidationErrors((prev) => ({ ...prev, organisatie: 'Organisatie is verplicht.' }));
  }

  const onChangeAutocomplete = async (event: any, newValue: any) => {
    // console.log('onChangeAutocomplete', newValue);
    if (!newValue) {
      resetBestuurlijkeOrganisatie();
      return;
    }

    const selectedOrganisatie = newValue;
    const objBestuurlijkeOrganisatie = bestuurlijkeOrganisaties.find((item) => item.code === selectedOrganisatie.code);
    if (!objBestuurlijkeOrganisatie) {
      resetBestuurlijkeOrganisatie();
      return;
    }
    else {
      setSelectedBestuurlijkeOrganisatie(objBestuurlijkeOrganisatie);
      
      const reference = objBestuurlijkeOrganisatie.reference;
      const name = objBestuurlijkeOrganisatie.name;
      setOrganisatie({ ...organisatie, name, reference  });
      setValidationErrors((prev) => ({ ...prev, organisatie: '' }));
    }
  };

  const onInputChangeAutocomplete = async (event: any, inputValue: any, reason: string) => {
    // console.log('onInputChangeAutocomplete', inputValue);
    if (reason === 'input' && inputValue.length > 2) {
      const delayDebounceFn = setTimeout(() => {
        fetchAutocompleteOptions(inputValue);
      }, 300); // Add debounce to reduce API calls
      return () => clearTimeout(delayDebounceFn);
    } 
    else {
      // setOptions([]); // Clear options if inputValue is empty
    }
  }

  const fetchAutocompleteOptions = async (inputValue: any) => {
    try {
      // Only if there is a type
      if (!type) return;
      if (type.toLowerCase() === 'waterschap' || type.toLowerCase() === 'netbeheerder') return; // type.toLowerCase() === 'organisatie' removed

      setLoading(true);
      const data = await bestuurlijkeGebiedenService.autocompleteByTypeAndName(autocompleteType, inputValue);
      if (data && data.length > 0) {
        // console.log('autocomplete data', data);
        setBestuurlijkeOrganisaties(data);
  
        const bestuurlijkGebiedType = data[0]?.bestuurlijkGebiedType;
        const capitalizedType = bestuurlijkGebiedType ? bestuurlijkGebiedType.charAt(0).toUpperCase() + bestuurlijkGebiedType.slice(1) : '';
        setOrganisatieType(capitalizedType);
      }
    } 
    catch (error: any) {
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      console.error('Error fetching autocomplete options:', error);
    } 
    finally {
      setLoading(false);
    }
  };

  const fetchRoutes = async () => {
    try {
      const data = await bestuurlijkeGebiedenService.getRoutes();
      setRoutes(data); // Assuming the API response is a JSON array of routes
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError({ id: Date.now(), message: error.message || 'Er is een fout opgetreden' });
    }
    finally {
      setLoading(false);
    }
  };

  
  useEffect(() => {
    if (openDialog) {
      // Ensure the fetch happens only once
      if (!hasRoutesData.current) {
        // console.log('Fetching routes, isZonderGebied:', isZonderGebied);
        if (isZonderGebied) {
          setOrganisatieType('zonder-gebied');
          setOrganisatie({ ...organisatie, organizationType: 'zonder-gebied', displayName: null });
          setLoading(false);
        }
        else {
          fetchRoutes();
        }  
        
        hasRoutesData.current = true; // Mark as fetched
      }
    }
    else {
      hasRoutesData.current = false; // Reset when dialog is closed
      setLoading(false);
    }
  }, [openDialog]); // Run evertime the dialog is opened/closed

  // useEffect(() => {
  //   console.log('Currentype: ', type);
  //   // console.log('Organisatie: ', organisatie);
  //   console.log('isZonderGebied: ', isZonderGebied);
  //   // console.log('ValidationErrors: ', validationErrors);
  //   console.log('hasRoutesData: ', hasRoutesData);
  // }, [type, organisatie, validationErrors, isZonderGebied]);

  return (
    <React.Fragment>
      <Tooltip title="Organisatie toevoegen" placement="right" arrow>
        <Button variant="text" color="secondary" onClick={ () => onClickOpenDialogAddOrganisatie(true) }>
          <Icon>add</Icon>
          Organisatie
        </Button>
      </Tooltip>

      <form>
        <Dialog
          className='dialog dialog-1000'
          open={openDialog}
          onClose={handleClose}
          PaperProps={{
            component: 'form',
            sx: {
              minWidth: 800,
              maxWidth: 1000,
              margin: '0 auto', // center horizontally
            },
            // onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            //   event.preventDefault();
            //   const formData = new FormData(event.currentTarget);
            //   const formJson = Object.fromEntries(formData.entries());
            //   const email = formJson.email;
            //   console.log(email);
            //   onClickOpenDialogAddOrganisatie(false);
            // },
          }}
        >
          <DialogTitle id="dialog-organisation-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Organisatie toevoegen
            <IconButton aria-label="close" onClick={ () => onClickOpenDialogAddOrganisatie(false) } size="small">
              <GridCloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>

            <DialogContentText>
              { !isZonderGebied ? (
                <>
                  Kies eerst een bestuurlijk gebied waarvoor je een organisatie wilt toevoegen en vervolgens de organisatie. 
                  Wilt u een provincie, gemeente, wijk of buurt kies dan eerst de bovenliggende bestuurlijke eenheid.
                  Kies vervolgens nog een naam die wordt gebruikt voor weergave.
                </>
              ) : (
                <>
                  Kies een organisatie naam en een weergave naam.
                </>
              )}
            </DialogContentText>
            <br/>
            
            { !isZonderGebied && (
              <section>
                <label>Kies het bestuurlijke gebied</label>
                <br/>
                <FormControl variant="filled" sx={{ my: 1, minWidth: 400 }}>
                  <InputLabel id="select-route-label">Bestuurlijke gebied</InputLabel>
                  <Select
                    id="select-route"
                    labelId="select-route-label"
                    autoWidth
                    value={objRoute?.route ? String(objRoute?.route) : ""} // Make sure selectedRoute is a string or the same type as item.route
                    // value={String(objRoute?.route) || ''} 
                    onChange={onChangeRoute}
                    error={!!validationErrors.route}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          minWidth: 400, // Zorgt dat het menu net zo breed is als de FormControl
                        },
                      },
                    }}
                    >
                    <MenuItem value="">
                      <em>&nbsp;</em>
                    </MenuItem>
                    {routes.map((item) => (
                      <MenuItem key={item.route} value={String(item.route)}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.route && (
                    <span style={{ color: '#d32f2f', fontSize: '0.8rem' }}>
                    {validationErrors.route}
                  </span>
                  )}
                </FormControl>

                { pathOrganisaties && pathOrganisaties.length > 0 && (
                  // <NavigateNextIcon fontSize="small" />
                  <div role="presentation">
                    <label>Gekozen bestuurlijke route</label>
                    <Breadcrumbs className='breadcrumbs' aria-label="breadcrumb" separator="/">
                      {pathOrganisaties.map((organisatie) => (
                        <Chip
                          label={`${organisatie.bestuurlijkGebiedType}: ${organisatie.name}`}
                          onDelete={ () => onClickDeletePathOrganisatie(organisatie) }
                        />
                      ))}
                    </Breadcrumbs>
                  </div>
                )}

              </section>
            )}
            
            { type && (
              <>
                {!isZonderGebied && type !== 'zonder-gebied' && (
                  <section>
                    <label>Kies de {type.toLowerCase()} die je wilt toevoegen</label>
                    <br/>

                    <div className="row">
                      <div className="col-6">
                        <Autocomplete
                          options={bestuurlijkeOrganisaties}
                          getOptionLabel={(option) => `${option.name}`}
                          loadingText="Laden..."
                          renderOption={(props, option) => (
                            <li {...props}>
                              {option.name} | {option.code}
                            </li>
                          )}
                          value={selectedBestuurlijkeOrganisatie || null}
                          onInputChange={onInputChangeAutocomplete}
                          onChange={onChangeAutocomplete}
                          renderInput={(params) => (
                            <TextField 
                              sx={{ my: 1, minWidth: 400 }}
                              {...params}
                              label={type}
                              variant="filled"
                              error={!!validationErrors.organisatie}
                              helperText={validationErrors.organisatie}
                            />
                          )}
                          isOptionEqualToValue={(option, value) => option.code === value.code}
                        />

                      </div>
                      <div className="col-6"></div>
                      <div className="col-12">

                        {/* { selectedBestuurlijkeOrganisatie && (
                          <pre><code>{JSON.stringify(selectedBestuurlijkeOrganisatie)}</code></pre>
                        )} */}

                        { selectedBestuurlijkeOrganisatie && 
                          (selectedBestuurlijkeOrganisatie.nextUrl || (!selectedBestuurlijkeOrganisatie.nextUrl && selectedBestuurlijkeOrganisatie.bestuurlijkGebiedType === 'buurt')) && (
                          <Button variant="text" color="secondary" onClick={ () => onClickSelectNextUrl() }>
                            { selectedBestuurlijkeOrganisatie.nextUrl && (
                              <>Kies { selectedBestuurlijkeOrganisatie.name } en ga verder naar de { selectedBestuurlijkeOrganisatie.nextUrl.split('/').pop() }</>
                            )}

                            { !selectedBestuurlijkeOrganisatie.nextUrl && (
                              <>Kies { selectedBestuurlijkeOrganisatie.name }</>
                            )}
                            <Icon>chevron_right</Icon>
                          </Button>
                        )}
                      
                      </div>
                    </div>
                  </section>
                )}

                {isZonderGebied && type === 'zonder-gebied' && (
                  <section>
                    <label>
                      Vul een organisatie naam in
                    </label>
                    <br/>
                    <TextField 
                      sx={{ my: 1, minWidth: 400 }}
                      fullWidth
                      id="organisatie" 
                      label="Naam" 
                      variant="filled" 
                      onChange={handleChangeName}
                      error={!!validationErrors.organisatie}
                      helperText={validationErrors.organisatie}
                      value={organisatie?.name || ''}
                      />
                    <br/>
                  </section>
                )}

                <section>
                  <label>
                    Vul een naam in die wordt gebruikt voor weergave 
                    { !isZonderGebied && type !== 'zonder-gebied' && (
                      <span>&nbsp;van de {type.toLowerCase()}</span>
                    )}
                  </label>
                  <br/>
                  <TextField 
                    sx={{ my: 1, minWidth: 400 }}
                    fullWidth
                    // sx={{ mt: 2 }}
                    id="displayname" 
                    label="Weergave naam" 
                    variant="filled" 
                    onChange={handleChangeDisplayName}
                    error={!!validationErrors.displayName}
                    value={organisatie?.displayName || ''}
                    helperText={validationErrors.displayName}
                    />
                  <br/>

                  {!isZonderGebied && organisatie && organisatie.reference && (
                    <div>
                      <label>Onderstaande {type.toLowerCase()} toevoegen?</label>
                      <pre style={{ background: '#f4f4f4', padding: '10px', borderRadius: '5px', fontSize: '12px' }}>{JSON.stringify(organisatie, null, 2)}</pre>
                      <p className="note">Klik op opslaan om deze organisatie toe te voegen.</p>
                    </div>
                  )}

                  {isZonderGebied && organisatie && (
                    <div>
                      <label>Onderstaande organisatie zonder gebied toevoegen?</label>
                      <pre style={{ background: '#f4f4f4', padding: '10px', borderRadius: '5px', fontSize: '12px' }}>{JSON.stringify(organisatie, null, 2)}</pre>
                      <p className="note">Klik op opslaan om deze organisatie toe te voegen.</p>
                    </div>
                  )}
                  
                </section>
              </>
            )}

            {validationErrors && (validationErrors.route || validationErrors.organisatie || validationErrors.displayName) && (
              <div>
                <label>Errors </label>
                <pre style={{ background: '#cc0000', color: '#ffffff', padding: '10px', borderRadius: '5px', fontSize: '12px' }}>{JSON.stringify(validationErrors, null, 2)}</pre>
              </div>
            )}
            
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            {/* <Button 
              color="secondary" 
              onClick={ () => onClickOpenDialogActivateOrganisatie(false) }>
              Sluiten
            </Button> */}
            <Button 
              type="button"
              onClick={handleSubmit}
              variant="contained" 
              color="secondary" 
              // disabled={!objRoute || !objBestuurlijkeOrganisatie || !organisatie?.displayName}
              disabled={Object.values(validationErrors).some((error) => error !== '')}>
                Opslaan
            </Button>
          </DialogActions>
        </Dialog>
      </form>

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage key={error.id} message={error.message} />
      )}

    </React.Fragment>
  );
};

export default DialogOrganisatieAdd;
